import { useRemoteData, remoteDataMapper } from "@sinch/mfe-helper-react";
import type { DeepPartial } from "simplytyped";
import { z } from "zod";
import { apiUrls } from "../api";
import { useFetchUser } from "./useUser";

export const partnerZod = z.object({
  accountId: z.string(),
  accountName: z.string(),
  allowLoginUsingSso: z.boolean().nullable(),
  allowRegistration: z.boolean().nullable(),
  description: z.string(),
  documentationUrl: z.string(),
  domainName: z.string().nullable(),
  ecosystem: z.string().nullable(),
  email: z.string().nullable(),
  emailToSmsDomain: z.string().nullable(),
  logoUrl: z.string().url(),
  portalLogoUrl: z.string().url(),
  slug: z.string(),
  supportArticleUrls: z.string().nullable(),
});

export type Partner = z.infer<typeof partnerZod>;

export const usePartner = () => {
  const user = useFetchUser().mapResult(remoteDataMapper.getData());
  const businessId = user?.account?.businessId || null;

  const partnerRemoteData = useRemoteData(
    `${apiUrls.partnerUrl}/${businessId}`,
    {
      enabled: !!businessId,
      useToken: true,
      parse: (data) => {
        const parsed = partnerZod.safeParse(data);
        if (parsed.success) return parsed.data;
        const errorMessage = "Invalid response from partners";
        console.error(errorMessage, parsed.error);
        return data as DeepPartial<Partner>;
      },
    }
  );

  return partnerRemoteData.mapResult({
    onInit: () => ({
      status: "init" as const,
    }),
    onLoading: () => ({
      isFetching: !!businessId,
      status: "loading" as const,
    }),
    onData: (data) => ({
      data,
      status: "success" as const,
    }),
    onError: (e) => ({
      e,
      status: "error" as const,
    }),
  });
};
